import { create } from 'zustand';
import { healthApi, type HealthResponse, type ReadyResponse } from '@/services/api';

interface HealthState {
  health: HealthResponse | null;
  ready: ReadyResponse | null;
  /** True after the first health check attempt completes (success or failure). */
  initialized: boolean;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;

  fetchHealth: () => Promise<void>;
  fetchReady: () => Promise<void>;
  fetchAll: () => Promise<void>;
}

export const useHealthStore = create<HealthState>((set) => ({
  health: null,
  ready: null,
  initialized: false,
  loading: false,
  error: null,
  lastFetched: null,

  fetchHealth: async () => {
    try {
      const health = await healthApi.check();
      set({ health, initialized: true });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Health check failed', initialized: true });
    }
  },

  fetchReady: async () => {
    try {
      const ready = await healthApi.ready();
      set({ ready });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Readiness check failed' });
    }
  },

  fetchAll: async () => {
    set({ loading: true, error: null });
    try {
      const [health, ready] = await Promise.all([
        healthApi.check(),
        healthApi.ready(),
      ]);
      set({ health, ready, lastFetched: Date.now(), initialized: true });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to fetch status', initialized: true });
    } finally {
      set({ loading: false });
    }
  },
}));
